/**
 * EmailService
 * Service for email communication operations
 * Route: api/email
 */
import { BaseService } from '../base/BaseService';
/**
 * EmailService
 * Handles email sending and management operations
 */
export class EmailService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? EmailService.DEFAULT_SERVICE_PATH,
            basePath: EmailService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== READ OPERATIONS ====================
    /**
     * Get all emails with caching
     * GET api/email
     */
    async getAll() {
        const response = await this.getAllRaw();
        return response.data ?? [];
    }
    /**
     * Get all emails with full response
     */
    getAllRaw() {
        const url = this.buildUrl();
        return this.http.get(url);
    }
    /**
     * Get email by ID
     * GET api/email/{id}
     */
    async getById(id) {
        const response = await this.getByIdRaw(id);
        if (!response.data) {
            throw new Error(`Email ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get email by ID with full response
     */
    getByIdRaw(id) {
        const url = this.buildUrl(String(id));
        return this.http.get(url);
    }
    /**
     * Get unsent emails
     * GET api/email/unsent
     */
    async getUnsent() {
        const response = await this.getUnsentRaw();
        return response.data ?? [];
    }
    /**
     * Get unsent emails with full response
     */
    getUnsentRaw() {
        const endpoint = this.getEndpoint('unsent', 'unsent');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    // ==================== WRITE OPERATIONS ====================
    /**
     * Create a new email
     * POST api/email
     */
    async create(email) {
        const response = await this.createRaw(email);
        if (!response.data) {
            throw new Error(response.message || 'Failed to create email');
        }
        return response.data;
    }
    /**
     * Create email with full response
     */
    createRaw(email) {
        const url = this.buildUrl();
        return this.http.post(url, email);
    }
    /**
     * Update an email
     * PUT api/email/{id}
     */
    async update(id, email) {
        const response = await this.updateRaw(id, email);
        if (!response.data) {
            throw new Error(response.message || 'Failed to update email');
        }
        return response.data;
    }
    /**
     * Update email with full response
     */
    updateRaw(id, email) {
        const url = this.buildUrl(String(id));
        return this.http.put(url, email);
    }
    /**
     * Delete an email
     * DELETE api/email/{id}
     */
    async delete(id) {
        const response = await this.deleteRaw(id);
        return response.success;
    }
    /**
     * Delete email with full response
     */
    deleteRaw(id) {
        const url = this.buildUrl(String(id));
        return this.http.delete(url);
    }
    // ==================== VIEW OPERATIONS ====================
    /**
     * Get all email views (explicit view endpoint)
     * GET api/email/view
     */
    async getViews() {
        const response = await this.getViewsRaw();
        return response.data ?? [];
    }
    /**
     * Get email views with full response
     */
    getViewsRaw() {
        const endpoint = this.getEndpoint('view', 'view');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get email view by ID
     * GET api/email/view/{id}
     */
    async getViewById(id) {
        const response = await this.getViewByIdRaw(id);
        if (!response.data) {
            throw new Error(`Email ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get email view by ID with full response
     */
    getViewByIdRaw(id) {
        const endpoint = this.getEndpoint('view-by-id', `view/${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    // ==================== QUEUE OPERATIONS ====================
    /**
     * Mark email as sent
     * POST api/email/{id}/mark-sent
     */
    async markSent(id) {
        const response = await this.markSentRaw(id);
        return response.success;
    }
    /**
     * Mark sent with full response
     */
    markSentRaw(id) {
        const endpoint = this.getEndpoint('mark-sent', `${id}/mark-sent`);
        const url = this.buildUrl(endpoint);
        return this.http.post(url, {});
    }
    // ==================== CACHE OPERATIONS ====================
    /**
     * Invalidate email cache
     * POST api/email/cache/invalidate
     */
    async invalidateCache() {
        const response = await this.invalidateCacheRaw();
        return response.success;
    }
    /**
     * Invalidate cache with full response
     */
    invalidateCacheRaw() {
        const endpoint = this.getEndpoint('cache-invalidate', 'cache/invalidate');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, {});
    }
}
EmailService.DEFAULT_SERVICE_PATH = 'communications';
EmailService.BASE_PATH = 'api/email';
//# sourceMappingURL=EmailService.js.map